import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { createVariants, cn } from '../../lib/utils';
import { SpinnerIcon } from './Icons';

// Button variants using your theme system
const buttonVariants = createVariants(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50",
  {
    variant: {
      default: "", // Will be filled by theme
      secondary: "", // Will be filled by theme
      accent: "", // Will be filled by theme
      danger: "", // Will be filled by theme
      warning: "", // Will be filled by theme
      info: "", // Will be filled by theme
      outline: "", // Will be filled by theme
      ghost: "hover:opacity-80",
      link: "underline-offset-4 hover:underline p-0"
    },
    size: {
      default: "px-4 py-2 h-10",
      sm: "px-3 py-1.5 h-8 text-xs",
      lg: "px-6 py-3 h-12 text-base",
      icon: "h-10 w-10 p-0"
    }
  },
  {
    variant: "default",
    size: "default"
  }
);

const Button = React.forwardRef(({ 
  className, 
  variant = "default", 
  size = "default", 
  children, 
  disabled,
  loading,
  ...props 
}, ref) => {
  const { theme } = useTheme();
  
  // Get base variant classes
  const baseClasses = buttonVariants({ variant, size, className });
  
  // Apply theme-specific styling based on variant
  const getVariantClasses = () => {
    switch (variant) {
      case 'default':
      case 'primary':
        return `${theme.colors.primary} ${theme.text.inverse} hover:opacity-90 ${theme.shadow.sm}`;
      case 'secondary':
        return `${theme.colors.secondary} ${theme.text.inverse} hover:opacity-90 ${theme.shadow.sm}`;
      case 'accent':
        return `${theme.colors.accent} ${theme.text.inverse} hover:opacity-90 ${theme.shadow.sm}`;
      case 'danger':
        return `${theme.colors.danger} ${theme.text.inverse} hover:opacity-90 ${theme.shadow.sm}`;
      case 'warning':
        return `${theme.colors.warning} ${theme.text.inverse} hover:opacity-80 ${theme.shadow.sm}`;
      case 'info':
        return `${theme.colors.info} ${theme.text.inverse} hover:opacity-80 ${theme.shadow.sm}`;
      case 'outline':
        return `${theme.colors.surface} ${theme.text.primary} ${theme.border.light} border hover:${theme.colors.background} ${theme.shadow.sm}`;
      case 'ghost':
        return `${theme.text.primary} hover:${theme.colors.background}`;
      case 'link':
        return `${theme.text.primary}`;
      default:
        return `${theme.colors.primary} ${theme.text.inverse} hover:opacity-90 ${theme.shadow.sm}`;
    }
  };
  
  const variantClasses = getVariantClasses();
  const finalClasses = cn(baseClasses, variantClasses);
  
  return (
    <button
      className={finalClasses}
      ref={ref}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <SpinnerIcon className="w-4 h-4 mr-2" />
      )}
      {children}
    </button>
  );
});

Button.displayName = "Button";

export { Button, buttonVariants };
